import React, { useState, useEffect, useRef } from 'react';
import { db, storage, auth } from '../firebase';
import { doc, onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { v4 as uuidv4 } from 'uuid';
import { formatFieldDisplayValue } from '../helpers/productChatConfig';
import { userCanAccessQuote } from '../helpers/quoteVisibility';

const QuoteDetails = () => {
  const { id } = useParams();
  const [realTimeQuote, setRealTimeQuote] = useState(null);
  const [currentUserUid, setCurrentUserUid] = useState('');
  const [selectedTab, setSelectedTab] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const navigate = useNavigate();
  const printRef = useRef();
  const [vendorName, setVendorName] = useState('');
  const [vendorPdf, setVendorPdf] = useState(null);
  const [productType, setProductType] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [userRole, setUserRole] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [artworkLoading, setArtworkLoading] = useState(false);
  const [bottleImageLoading, setBottleImageLoading] = useState(true);
  const [isVendor, setIsVendor] = useState(false);
  const [roleFetched, setRoleFetched] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        setCurrentUserUid(user.uid);
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setUserRole(userData.role || '');
          setIsVendor(userData.vendor);
        }
        setRoleFetched(true);
      }
    };
    fetchUserRole();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'QuoteRequirements', id), (docSnap) => {
      const data = docSnap.data();
      if (!data) {
        setRealTimeQuote(null);
        return;
      }
      setRealTimeQuote({ id: docSnap.id, ...data });
      if (data.products && data.products.length > 0) {
        setSelectedTab(data.products[0].productType);
      }
      
      // Check access: admin, creator, or referring sales rep
      if (currentUserUid && roleFetched) {
        const canView = userCanAccessQuote(
          { id: docSnap.id, ...data },
          { currentUserUid, userRole },
        );
        setHasAccess(canView);

        if (!canView) {
          navigate('/packaging-details');
        }
      }
    });

    return () => unsubscribe();
  }, [id, currentUserUid, userRole, roleFetched, navigate]);

  const handleQuantityChange = (e, productIndex, skuIndex, quantityIndex) => {
    const newQuote = { ...realTimeQuote };
    newQuote.products[productIndex].skuDetails[skuIndex].quantities[quantityIndex].value = e.target.value;
    setRealTimeQuote(newQuote);
  };

  const handleSaveQuantities = async () => {
    setIsSaving(true);
    if (realTimeQuote) {
      const quoteRef = doc(db, 'QuoteRequirements', realTimeQuote.id);
      await updateDoc(quoteRef, {
        'products': realTimeQuote.products
      });

      const subject = 'Quote Updated';
      const textBody = `The quote with ID ${realTimeQuote.id} has been updated.`;
      const htmlBody = `
        <div style="font-family: Arial, sans-serif; margin: 20px;">
          <h2 style="color: #333;">Quote Updated</h2>
          <p style="font-size: 16px; color: #555;">
            The quote with ID ${realTimeQuote.id} has been updated. Click the button below to view the details:
          </p>
          <a href="https://shipping-quote.labelslab.com/packaging-details/${realTimeQuote.id}" style="display: inline-block; margin-top: 20px; padding: 10px 20px; color: white; background-color: #007BFF; text-decoration: none; border-radius: 5px;">
            View the Quote
          </a>
          <p style="font-size: 14px; color: #999; margin-top: 20px;">
            If you have any questions, please contact us at <a href="mailto:vaibhav@designersprescription.com" style="color: #007BFF;">vaibhav@designersprescription.com</a>.
          </p>
        </div>
      `;

      await notifyMaria(realTimeQuote.id, subject, textBody, htmlBody);
    }
    setIsSaving(false);
    setIsSaved(true);
    setIsEditing(false);
    setTimeout(() => setIsSaved(false), 3000);
  };

  const notifyMaria = async (quoteId, subject, textBody, htmlBody) => {
    try {
      const response = await fetch('https://ghft6mowc4.execute-api.us-east-2.amazonaws.com/default/QuoteForm-EmailSender', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipientEmail: 'maria@labelslab.com',
          subject: subject,
          textBody: textBody,
          htmlBody: htmlBody
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to send email: ${response.status} ${response.statusText}. ${errorText}`);
      }

      console.log('Email sent successfully to Maria.');
    } catch (error) {
      console.error('Error notifying Maria:', error);
      // Optionally show a toast notification to the user
      alert('Failed to send notification email. The changes were saved successfully.');
    }
  };

  const formatFieldName = (fieldName) => {
    return fieldName
      .split(/(?=[A-Z])/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const maskVendorText = (value) => {
    const text = String(value || '').trim();
    if (!text) return 'N/A';
    if (text.length === 1) return `${text}***`;
    return `${text[0]}***${text[text.length - 1]}`;
  };

  const handleDownloadPDF = async () => {
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pageHeight = pdf.internal.pageSize.height;
    const pageWidth = pdf.internal.pageSize.width;

    // VIP Banner at the very top
    let pdfTopOffset = 0;
    if (realTimeQuote.isVIPClient) {
      pdf.setFillColor(255, 215, 0);
      pdf.rect(0, 0, pageWidth, 28, 'F');
      pdf.setFontSize(13);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(100, 60, 0);
      pdf.text('VIP Customer — Please Handle With Priority', pageWidth / 2, 18, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
      pdfTopOffset = 32;
    }

    // 1. Project Description at the top in bold red
    if (realTimeQuote.projectDescription) {
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(255, 0, 0);
      pdf.text(realTimeQuote.projectDescription, 40, 40 + pdfTopOffset, { maxWidth: pageWidth - 80 });
      pdf.setTextColor(0, 0, 0);
    }
    let currentY = realTimeQuote.projectDescription ? 60 + pdfTopOffset : 40 + pdfTopOffset;

    try {
        if (!realTimeQuote || !realTimeQuote.products) {
            console.error('Quote data is missing or incomplete');
            return;
        }

        const filteredProducts = realTimeQuote.products.filter(product => product.productType === selectedTab);

        for (let productIndex = 0; productIndex < filteredProducts.length; productIndex++) {
            const product = filteredProducts[productIndex];
            const skuDetails = Array.isArray(product.skuDetails) ? product.skuDetails : [];
            const firstSkuQuantities = Array.isArray(skuDetails[0]?.quantities) ? skuDetails[0].quantities : [];
            const customerDisplayName = realTimeQuote.customerName || realTimeQuote.companyName || 'N/A';

            if (productIndex > 0) {
                pdf.addPage();
            }

            

            pdf.setFontSize(20);
            pdf.setFont(undefined, 'bold');
            pdf.text(`Product ${productIndex + 1}: ${product.productType || ''}`, 40, 100);

            pdf.setFontSize(12);
            pdf.setFont(undefined, 'normal');
            pdf.autoTable({
                startY: 120,
                head: [['Field', 'Value']],
                body: [
                    [
                        'Customer Name',
                        isVendor ? maskVendorText(customerDisplayName) : customerDisplayName,
                    ],
                    ['Sales Rep Name', realTimeQuote.salesRepName || ''],
                    [
                        'Project Name',
                        isVendor
                            ? maskVendorText(realTimeQuote.projectName)
                            : realTimeQuote.projectName || '',
                    ],
                    ['Project ID', realTimeQuote.projectId || ''],
                    [
                        'Printing Style',
                        product.printingStyle === 'Both' ? 'CMYK & PMS Both' : product.printingStyle || '',
                    ],
                    ['Production Type', product.productionType || ''],
                    ['SKU Count', product.SKU || ''],
                    ['Quantities Per SKU', product.quantitiesCount || ''],
                    ['Color Match', product.colorMatch || ''],
                    ['Is this a Reprint?', product.isReprint || ''],
                    ...(product.isReprint === "Yes" && product.previousInvoiceNumber ? [
                        ['Previous Invoice Number', product.previousInvoiceNumber]
                    ] : []),
                    ...(product.colorMatch === "Yes" ? [
                        ['Color Match Order Number', product.colorMatchOrderNumber || ''],
                        ['Color Match Details', product.colorMatchDetails || '']
                    ] : [])
                ],
                theme: 'grid',
                styles: { fontSize: 10 },
            });

// Notes to Production
            const notesToProductionY = pdf.lastAutoTable.finalY + 20;
pdf.setFont(undefined, 'bold');
pdf.setTextColor(255, 0, 0);
pdf.text('Notes to Production:', 40, notesToProductionY);
pdf.setTextColor(0, 0, 0);
pdf.setFont(undefined, 'normal');
pdf.text(product.specialNotesToProduction || 'No notes to production', 180, notesToProductionY);

            const fieldsTableYStart = pdf.lastAutoTable.finalY + 80;
            const productFields = Object.entries(product.fields || {})
                .filter(([key]) => key !== 'bottleImage' && key !== 'artwork' && key !== 'referenceImageUrl')
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => {
                    const formattedName = formatFieldName(key);
                    let displayValue;
                    if (Array.isArray(value)) {
                        displayValue = value.join(', ') || '-';
                    } else if (typeof value === 'object' && value !== null) {
                        const mmMeasurements = [];
                        const inMeasurements = [];
                        
                        // Millimeters
                        if (value.widthMM) mmMeasurements.push(`Width: ${value.widthMM}mm`);
                        if (value.heightMM) mmMeasurements.push(`Height: ${value.heightMM}mm`);
                        if (value.lengthMM) mmMeasurements.push(`Length: ${value.lengthMM}mm`);
                        if (value.gussetMM) mmMeasurements.push(`Gusset: ${value.gussetMM}mm`);
                        if (value.depthMM) mmMeasurements.push(`Depth: ${value.depthMM}mm`);
                        if (value.diameterMM) mmMeasurements.push(`Diameter: ${value.diameterMM}mm`);
                        if (value.openingMM) mmMeasurements.push(`Opening: ${value.openingMM}mm`);
                        if (value.thicknessMM) mmMeasurements.push(`Thickness: ${value.thicknessMM}mm`);

                        // Inches
                        if (value.width) inMeasurements.push(`Width: ${value.width}in`);
                        if (value.height) inMeasurements.push(`Height: ${value.height}in`);
                        if (value.length) inMeasurements.push(`Length: ${value.length}in`);
                        if (value.gusset) inMeasurements.push(`Gusset: ${value.gusset}in`);
                        if (value.depth) inMeasurements.push(`Depth: ${value.depth}in`);
                        if (value.diameter) inMeasurements.push(`Diameter: ${value.diameter}in`);
                        if (value.opening) inMeasurements.push(`Opening: ${value.opening}in`);
                        if (value.thickness) inMeasurements.push(`Thickness: ${value.thickness}in`);

                        displayValue = [
                            'Millimeters:',
                            ...mmMeasurements,
                            '',
                            'Inches:',
                            ...inMeasurements
                        ].join('\n');
                    } else {
                        const formatted = formatFieldDisplayValue(key, value);
                        displayValue = formatted || value || '-';
                    }
                    return [formattedName, displayValue];
                });

            productFields.push(['Number of SKU', skuDetails.length || product.SKU || 0]);

            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Product Fields', 80, fieldsTableYStart - 10);
            pdf.setFont(undefined, 'normal');
            pdf.autoTable({
                startY: fieldsTableYStart,
                head: [['Field', 'Value']],
                body: productFields.length ? productFields : [['-', 'No product fields']],
                theme: 'grid',
                styles: { fontSize: 10 },
            });

            // Handle reference image for Other product type
            let referenceImageHeight = 0;
            if (product.productType === 'Other' && product.fields?.referenceImageUrl) {
                const referenceImageY = pdf.lastAutoTable.finalY + 30;
                pdf.setFontSize(12);
                pdf.setFont(undefined, 'bold');
                pdf.text('Reference Image:', 40, referenceImageY);
                pdf.setFont(undefined, 'normal');
                
                try {
                    console.log(`Loading reference image: ${product.fields.referenceImageUrl}`);
                    const imgResp = await fetch(product.fields.referenceImageUrl);
                    if (!imgResp.ok) {
                        throw new Error(`Failed to fetch reference image: ${imgResp.status} ${imgResp.statusText}`);
                    }
                    const imgBlob = await imgResp.blob();
                    const reader = new FileReader();
                    await new Promise((resolve) => {
                        reader.onloadend = resolve;
                        reader.readAsDataURL(imgBlob);
                    });
                    const base64data = reader.result;
                    await new Promise((resolve) => {
                        const tempImg = new window.Image();
                        tempImg.onload = function () {
                          const aspectRatio = tempImg.height / tempImg.width;
                          let imgWidth = 300; // Reasonable size
                          let imgHeight = imgWidth * aspectRatio;
                          
                          // Only scale down width if it would exceed page width
                          const maxWidth = pageWidth - 80; // Leave some margin
                          if (imgWidth > maxWidth) {
                            imgWidth = maxWidth;
                            imgHeight = imgWidth * aspectRatio;
                          }
                          
                          // Store the height for positioning subsequent content
                          // Calculate the actual space from the reference image Y position to the end of the link
                          referenceImageHeight = (referenceImageY + imgHeight + 20) - pdf.lastAutoTable.finalY;
                          
                          console.log(`Reference image - Original: ${tempImg.width}x${tempImg.height}, Final: ${imgWidth}x${imgHeight}, Aspect ratio: ${aspectRatio}`);
                          pdf.addImage(base64data, 'JPEG', 40, referenceImageY + 10, imgWidth, imgHeight);
                          pdf.setFontSize(8);
                          pdf.setTextColor(0, 0, 255);
                          pdf.textWithLink('Open Reference Image', 40, referenceImageY + imgHeight + 20, { url: product.fields.referenceImageUrl });
                          pdf.setTextColor(0, 0, 0);
                          resolve();
                        };
                        tempImg.src = base64data;
                    });
                } catch (error) {
                    console.error('Error loading reference image:', error);
                    pdf.text('Error loading reference image', 40, referenceImageY + 10);
                }
            }

            // Quantities Table
            const quantitiesTableYStart = pdf.lastAutoTable.finalY + 20 + referenceImageHeight;
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Quantities', 40, quantitiesTableYStart);
            pdf.setFont(undefined, 'normal');

            const quantityHeaders = firstSkuQuantities.length
              ? ['SKU', ...firstSkuQuantities.map((_, i) => `Quantity ${i + 1}`)]
              : ['SKU', 'Quantity 1'];
            const quantityBody = skuDetails.length
              ? skuDetails.map((skuDetail) => [
                  skuDetail.sku || '-',
                  ...(Array.isArray(skuDetail.quantities) && skuDetail.quantities.length
                    ? skuDetail.quantities.map((qty) => qty.value || '-')
                    : ['-']),
                ])
              : [['-', '-']];

            pdf.autoTable({
                startY: quantitiesTableYStart + 10,
                head: [quantityHeaders],
                body: quantityBody,
                theme: 'grid',
                styles: { fontSize: 10 },
            });

            // Empty Pricing Table
            const pricingTableYStart = pdf.lastAutoTable.finalY + 20 + referenceImageHeight;
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Pricing', 40, pricingTableYStart);
            pdf.setFont(undefined, 'normal');

            const pricingHeaders = firstSkuQuantities.length
              ? ['SKU', ...firstSkuQuantities.map((_, i) => `Price for Qty ${i + 1}`)]
              : ['SKU', 'Price for Qty 1'];
            const pricingBody = skuDetails.length
              ? skuDetails.map((skuDetail) => [
                  skuDetail.sku || '-',
                  ...(Array.isArray(skuDetail.quantities) && skuDetail.quantities.length
                    ? skuDetail.quantities.map(() => '')
                    : ['']),
                ])
              : [['-', '']];

            pdf.autoTable({
                startY: pricingTableYStart + 10,
                head: [pricingHeaders],
                body: pricingBody,
                theme: 'grid',
                styles: { fontSize: 10 },
            });

            // Printing Technology Table
            const printingTechnologyYStart = pdf.lastAutoTable.finalY + 40 + referenceImageHeight;
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Printing Technology', 40, printingTechnologyYStart);
            pdf.setFont(undefined, 'normal');

            const printingDetails = [
                ['Printing Type', '( ) Digital Printing   ( ) Plates Printing'],
                ['Total Plates', ''],
                ['Price Per Plate', ''],
                ['Plate Size', ''],
                ['Total Price of the Plates', ''],
            ];

            pdf.autoTable({
                startY: printingTechnologyYStart + 10,
                head: [['Field', 'Value']],
                body: printingDetails,
                theme: 'grid',
                styles: { fontSize: 10 },
            });

            // Air/Sea Shipping Quantities Table
            const airSeaQuantitiesTableYStart = pdf.lastAutoTable.finalY + 20 + referenceImageHeight;
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Air/Sea Shipping Quantities', 40, airSeaQuantitiesTableYStart);
            pdf.setFont(undefined, 'normal');

            const airSeaQuantitiesHeaders = ['SKU', 'Express Air Qty', 'Regular Air Qty', 'Regular Sea Qty', 'Express Sea Qty'];
            const airSeaQuantitiesBody = (skuDetails.length ? skuDetails : [{ sku: '-' }]).map((skuDetail) => [
                skuDetail.sku || '-',
                '', '', '', '', // Empty for manual entry
            ]);

            pdf.autoTable({
                startY: airSeaQuantitiesTableYStart + 10,
                head: [airSeaQuantitiesHeaders],
                body: airSeaQuantitiesBody,
                theme: 'grid',
                styles: { fontSize: 10 },
            });

            // Air/Sea Shipping Prices Table
            const airSeaPricesTableYStart = pdf.lastAutoTable.finalY + 20 + referenceImageHeight;
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Air/Sea Shipping Prices', 40, airSeaPricesTableYStart);
            pdf.setFont(undefined, 'normal');

            const airSeaPricesHeaders = ['SKU', 'Express Air Price', 'Regular Air Price', 'Regular Sea Price', 'Express Sea Price'];
            const airSeaPricesBody = (skuDetails.length ? skuDetails : [{ sku: '-' }]).map((skuDetail) => [
                skuDetail.sku || '-',
                '', '', '', '', // Empty for manual entry
            ]);

            pdf.autoTable({
                startY: airSeaPricesTableYStart + 10,
                head: [airSeaPricesHeaders],
                body: airSeaPricesBody,
                theme: 'grid',
                styles: { fontSize: 10 },
            });

            // Shipping Details Table
            const shippingDetailsYStart = pdf.lastAutoTable.finalY + 40 + referenceImageHeight;
            const shippingDetails = [
                { label: 'Commodity Type', value: '' },
                { label: 'From Shipping Address', value: '' },
                { label: 'Split Shipping With Breakdown', value: '' },
                { label: 'Palletize', value: '' },
                { label: 'Special Notes', value: product.shippingInstructions || '' },
                { label: 'Total Units Per Commodity', value: '' },
                { label: 'Number Of Units Per Box', value: '' },
                { label: 'Total Number Of Boxes Per Commodity', value: '' },
                { label: 'Ship Box Dimensions', value: '' },
                { label: 'Ship Weight Per Box', value: '' },
                { label: 'Total Qty Of Boxes Per Commodity', value: '' },
                { label: 'Shipping Volume Per Product', value: '' },
                { label: 'Total Volume For The Whole Order', value: '' },
            ];
            
          
            const shippingDetailsBody = shippingDetails.map(detail => [detail.label, detail.value]);
            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Shipping Details', 40, shippingDetailsYStart);
            pdf.setFont(undefined, 'normal');
            pdf.autoTable({
                startY: shippingDetailsYStart + 10,
                head: [['Field', 'Value']],
                body: shippingDetailsBody,
                theme: 'grid',
                styles: { fontSize: 10 },
                columnStyles: {
                    0: { cellWidth: 0.5 * pageWidth - 40 },
                    1: { cellWidth: 0.5 * pageWidth - 40 },
                },
            });
// --- Copy from here
                      // ✅ Insert this new section for additional questions
            // Calculate proper spacing after images
            let additionalQuestionsYStart;
            if (product.imageUrls && product.imageUrls.length > 0) {
              // If there are images, we need to calculate based on the actual image positions
              // We'll set this after the images are rendered
              additionalQuestionsYStart = null; // Will be set after images
            } else {
              // If no images, use normal spacing
              additionalQuestionsYStart = pdf.lastAutoTable.finalY + 40;
            }

            // ✅ Insert this inside the product loop, right after pdf.autoTable({...})
            if (product.imageUrls && product.imageUrls.length > 0) {
              let imagesY = pdf.lastAutoTable ? pdf.lastAutoTable.finalY + 30 : currentY + 30;
              pdf.setFontSize(12);
              pdf.setFont(undefined, 'bold');
              pdf.text('Product Images:', 40, imagesY);
              pdf.setFont(undefined, 'normal');
              let imgX = 40;
              let imgY = imagesY + 20;
              const imgWidth = 200;
              const gap = 20;
              let maxImageBottom = imgY; // Track the bottom of the last image
              
              for (let idx = 0; idx < product.imageUrls.length; idx++) {
                const url = product.imageUrls[idx];
                try {
                  // Detect file type from URL filename (same logic as page display)
                  const fileName = url.split('/').pop() || '';
                  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName);
                  const isPDF = /\.pdf$/i.test(fileName);
                  
                  if (isPDF) {
                    // For PDF files: Just add a clickable link, don't try to render
                    console.log(`Adding PDF link in PDF generation: ${url}`);
                    pdf.setFontSize(10);
                    pdf.setTextColor(0, 0, 255);
                    pdf.textWithLink(`PDF Document ${idx + 1} - Click to view`, imgX, imgY, { url: url });
                    pdf.setTextColor(0, 0, 0);
                    imgY += 20;
                    continue;
                  }
                  
                  if (!isImage) {
                    // For other non-image files: Add a link
                    pdf.setFontSize(10);
                    pdf.setTextColor(0, 0, 255);
                    pdf.textWithLink(`File ${idx + 1} - Click to view`, imgX, imgY, { url: url });
                    pdf.setTextColor(0, 0, 0);
                    imgY += 20;
                    continue;
                  }
                  
                  // For IMAGES: Try to render the image and add a link
                  const img = url;
                  
                  console.log(`Loading image ${idx + 1}/${product.imageUrls.length}: ${img}`);
                  
                  // Try to fetch with CORS support
                  let imgResp;
                  try {
                    imgResp = await fetch(img, { mode: 'cors', credentials: 'omit' });
                  } catch (fetchError) {
                    // If fetch fails, try using the image directly via Image object
                    console.log(`Fetch failed, trying direct image load: ${fetchError.message}`);
                    // eslint-disable-next-line no-loop-func
                    await new Promise((resolve, reject) => {
                      const tempImg = new window.Image();
                      tempImg.crossOrigin = 'anonymous';
                      tempImg.onload = async function () {
                        try {
                          // Try to convert canvas to base64
                          const canvas = document.createElement('canvas');
                          canvas.width = tempImg.width;
                          canvas.height = tempImg.height;
                          const ctx = canvas.getContext('2d');
                          ctx.drawImage(tempImg, 0, 0);
                          const base64data = canvas.toDataURL('image/jpeg', 0.9);
                          
                          const aspectRatio = tempImg.height / tempImg.width;
                          let finalWidth = imgWidth;
                          let finalHeight = imgWidth * aspectRatio;
                          
                          const maxWidth = pageWidth - imgX - 40;
                          if (finalWidth > maxWidth) {
                            finalWidth = maxWidth;
                            finalHeight = finalWidth * aspectRatio;
                          }
                          
                          pdf.addImage(base64data, 'JPEG', imgX, imgY, finalWidth, finalHeight);
                          pdf.setFontSize(8);
                          pdf.setTextColor(0, 0, 255);
                          pdf.textWithLink('Open Image', imgX, imgY + finalHeight + 10, { url: img });
                          pdf.setTextColor(0, 0, 0);
                          
                          maxImageBottom = Math.max(maxImageBottom, imgY + finalHeight + 25);
                          
                          imgX += finalWidth + gap;
                          if (imgX + imgWidth > pageWidth - 40) {
                            imgX = 40;
                            imgY += finalHeight + gap + 25;
                            if (imgY + finalHeight > pageHeight - 100) {
                              pdf.addPage();
                              imgY = 50;
                            }
                          }
                          resolve();
                        } catch (canvasError) {
                          console.log(`Canvas conversion failed: ${canvasError.message}`);
                          // Add link as fallback
                          pdf.setFontSize(8);
                          pdf.setTextColor(0, 0, 255);
                          pdf.textWithLink(`Image ${idx + 1} (View Online)`, imgX, imgY, { url: img });
                          pdf.setTextColor(0, 0, 0);
                          imgY += 20;
                          resolve();
                        }
                      };
                      tempImg.onerror = () => {
                        console.log(`Image load failed: ${img}`);
                        pdf.setFontSize(8);
                        pdf.setTextColor(0, 0, 255);
                        pdf.textWithLink(`Image ${idx + 1} (View Online)`, imgX, imgY, { url: img });
                        pdf.setTextColor(0, 0, 0);
                        imgY += 20;
                        resolve();
                      };
                      tempImg.src = img;
                    });
                    continue;
                  }
                  
                  if (!imgResp.ok) {
                    throw new Error(`Failed to fetch image: ${imgResp.status} ${imgResp.statusText}`);
                  }
                  
                  // Check content type
                  const contentType = imgResp.headers.get('content-type') || '';
                  if (!contentType.startsWith('image/')) {
                    console.log(`Skipping non-image file (${contentType}): ${img}`);
                    pdf.setFontSize(8);
                    pdf.setTextColor(0, 0, 255);
                    pdf.textWithLink(`File ${idx + 1}`, imgX, imgY, { url: img });
                    pdf.setTextColor(0, 0, 0);
                    imgY += 20;
                    continue;
                  }
                  
                  const imgBlob = await imgResp.blob();
                  const reader = new FileReader();
                  await new Promise((resolve) => {
                    reader.onloadend = resolve;
                    reader.readAsDataURL(imgBlob);
                  });
                  const base64data = reader.result;
                  // eslint-disable-next-line no-loop-func
                  await new Promise((resolve, reject) => {
                    const tempImg = new window.Image();
                    tempImg.onerror = () => {
                      console.log(`Failed to load image: ${img}`);
                      // Add link as fallback
                      pdf.setFontSize(8);
                      pdf.setTextColor(0, 0, 255);
                      pdf.textWithLink(`Image ${idx + 1} (View Online)`, imgX, imgY, { url: img });
                      pdf.setTextColor(0, 0, 0);
                      imgY += 20;
                      resolve();
                    };
                    tempImg.onload = function () {
                      const aspectRatio = tempImg.height / tempImg.width;
                      let finalWidth = imgWidth;
                      let finalHeight = imgWidth * aspectRatio;
                      
                      // Only scale down width if it would exceed page width
                      const maxWidth = pageWidth - imgX - 40;
                      if (finalWidth > maxWidth) {
                        finalWidth = maxWidth;
                        finalHeight = finalWidth * aspectRatio;
                      }
                      
                      // Don't constrain height - let it flow naturally
                      console.log(`Product image ${idx + 1} - Original: ${tempImg.width}x${tempImg.height}, Final: ${finalWidth}x${finalHeight}, Aspect ratio: ${aspectRatio}`);
                      pdf.addImage(base64data, 'JPEG', imgX, imgY, finalWidth, finalHeight);
                      // Add clickable link below the image
                      pdf.setFontSize(8);
                      pdf.setTextColor(0, 0, 255);
                      pdf.textWithLink('Open Image', imgX, imgY + finalHeight + 10, { url: img });
                      pdf.setTextColor(0, 0, 0);
                      
                      // Update maxImageBottom to track the actual bottom of images
                      maxImageBottom = Math.max(maxImageBottom, imgY + finalHeight + 25);
                      
                      imgX += finalWidth + gap;
                      if (imgX + imgWidth > pageWidth - 40) {
                        imgX = 40;
                        imgY += finalHeight + gap + 25; // Add extra space for the link
                        
                        // Check if we need a new page
                        if (imgY + finalHeight > pageHeight - 100) {
                          pdf.addPage();
                          imgY = 50; // Reset Y position for new page
                        }
                      }
                      resolve();
                    };
                    tempImg.src = base64data;
                  });
                } catch (error) {
                  console.log(`Error loading image ${idx + 1}: ${error.message}`);
                  // Add a link to the file instead of failing completely
                  pdf.setFontSize(8);
                  pdf.setTextColor(0, 0, 255);
                  pdf.textWithLink(`Image ${idx + 1} (View Online)`, imgX, imgY, { url: url });
                  pdf.setTextColor(0, 0, 0);
                  imgY += 20;
                }
              }
              
              // Set the additional questions position based on actual image positions
              additionalQuestionsYStart = maxImageBottom + 30;
            }

            // For color match images, if present:
            if (product.colorMatchImages && product.colorMatchImages.length > 0) {
              const colorMatchImagesY = pdf.lastAutoTable.finalY + 20;
              pdf.setFontSize(12);
              pdf.setFont(undefined, 'bold');
              pdf.text('Color Match Images:', 40, colorMatchImagesY);
              pdf.setFont(undefined, 'normal');

              const colorMatchDetails = product.colorMatchImages.map((img, idx) => ({
                label: `Color Match Image ${idx + 1}`,
                value: img
              }));

              pdf.autoTable({
                startY: colorMatchImagesY + 10,
                head: [['Image', 'Details']],
                body: colorMatchDetails,
                theme: 'grid',
                styles: { fontSize: 10 },
              });
            }

      

            const additionalQuestions = [
                { label: '1). What is the maximum quantity that can be printed within the minimum requirement to activate the machine?', value: '' },
                { label: '2). The cost for the minimum order to activate the machine with this Material?', value: '' },
                { label: '3). What is the width of the machine that you will use?', value: '' },
                { label: '4). What is the model of the machine that you will use?', value: '' },
                { label: '5). How many units can be fit within the width?', value: '' },
                { label: '6). What is the bleeding estimate?', value: '' },
                { label: '7). What is width recommendation to maximize the width?', value: '' },

            ];

            // Only render additional questions if we have a valid position
            if (additionalQuestionsYStart !== null) {
              // Check if we need a new page for additional questions
              if (additionalQuestionsYStart > pageHeight - 200) {
                pdf.addPage();
                additionalQuestionsYStart = 50;
              }
              
              pdf.setFontSize(12);
              pdf.setFont(undefined, 'bold');
              pdf.text('Additional Questions', 40, additionalQuestionsYStart);
              pdf.setFont(undefined, 'normal');

              const additionalQuestionsBody = additionalQuestions.map(question => [question.label, '____________________']); // Placeholder for answers

              pdf.autoTable({
                  startY: additionalQuestionsYStart + 10,
                  head: [['Question', 'Answer']],
                  body: additionalQuestionsBody,
                  theme: 'grid',
                  styles: { fontSize: 10 },
                  columnStyles: {
                      0: { cellWidth: 0.5 * pageWidth - 40 },
                      1: { cellWidth: 0.5 * pageWidth - 40 },
                  },
              });
            }

        }

        // ...after all pages are generated...
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(100);
          pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 100, pageHeight - 20);
        }

        pdf.save(`quote-details - ${selectedTab} - (${realTimeQuote.projectId}).pdf`);
    } catch (error) {
        console.error('Error generating PDF:', error);
    }
};


// Copy up until here



  const handleAddVendorDetails = async () => {
    if (!vendorName || !vendorPdf || !productType) {
      alert('Please enter vendor name, select product type, and upload a file.');
      return;
    }

    setIsUploading(true);

    const fileExtension = vendorPdf.name.split('.').pop();
    const uniqueFileName = `quote-details - ${productType} - (${realTimeQuote.projectId})-${uuidv4()}.${fileExtension}`;
    const fileRef = ref(storage, `vendorPdfs/${uniqueFileName}`);
    
    await uploadBytes(fileRef, vendorPdf);
    const fileUrl = await getDownloadURL(fileRef);

    const vendorDetails = { vendorName, fileUrl, productType };

    const updatedVendorDetails = realTimeQuote.vendorDetails ? [...realTimeQuote.vendorDetails, vendorDetails] : [vendorDetails];

    await updateDoc(doc(db, 'QuoteRequirements', id), {
      vendorDetails: updatedVendorDetails
    });

    setVendorName('');
    setVendorPdf(null);
    setProductType('');
    setIsUploading(false);
  };

  // Wait for quote data and role verification
  if (!realTimeQuote || !roleFetched || !currentUserUid) {
    return <div>Loading...</div>;
  }
  
  // Final access check - if user doesn't have access, show nothing (redirect will happen)
  if (!userCanAccessQuote(realTimeQuote, { currentUserUid, userRole })) {
    return <div>You don't have access to this quote.</div>;
  }

  const groupedProducts = Array.isArray(realTimeQuote.products)
    ? realTimeQuote.products.reduce((acc, product) => {
        const { productType } = product;
        if (!acc[productType]) {
          acc[productType] = [];
        }
        acc[productType].push(product);
        return acc;
      }, {})
    : {};

    

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      {realTimeQuote.isVIPClient && (
        <div className="bg-yellow-400 text-yellow-900 font-bold text-center py-3 px-4 rounded-xl mb-4 text-lg shadow-md flex items-center justify-center gap-2">
          ⭐ VIP Customer — Please Handle With Priority ⭐
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-10 max-w-full">
        <div className="text-xl font-bold border-l-4 border-blue-500 pl-3 mb-4 text-blue-900">
          Quote Details
        </div>
        <div className="mb-4">
          {Object.keys(groupedProducts).map((productType) => (
            <button
              key={productType}
              className={`px-4 py-2 mr-2 border border-transparent text-sm font-medium rounded-md ${selectedTab === productType ? 'bg-blue-500 text-white' : 'bg-gray-300 text-black'}`}
              onClick={() => setSelectedTab(productType)}
            >
              {productType}
            </button>
          ))}
        </div>
        <div ref={printRef} className="p-6 max-w-full overflow-x-auto">
          {groupedProducts[selectedTab]?.map((product, productIndex) => (
            <div key={productIndex} className="mb-10">
              <div className="text-lg font-semibold mb-4">Quote Requirements - {product.productType} {productIndex + 1}</div>

              {product.imageUrls && product.imageUrls.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-4">
                  {product.imageUrls.map((url, idx) => {
                    // Detect file type from URL filename (same logic as StepOne)
                    const fileName = url.split('/').pop() || '';
                    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(fileName);
                    const isPDF = /\.pdf$/i.test(fileName);
                    
                    return (
                      <div key={idx} className="relative">
                        {isImage ? (
                          // Render image preview
                          <img
                            src={url}
                            alt={`Product ${idx + 1}`}
                      className="w-[150px] h-[150px] object-cover rounded-lg shadow hover:scale-105 transition-transform duration-200 border border-gray-200 m-2"
                            onError={(e) => {
                              // If image fails to load, show a placeholder with link
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              if (parent && !parent.querySelector('.image-error')) {
                                const errorDiv = document.createElement('div');
                                errorDiv.className = 'image-error w-[150px] h-[150px] bg-gray-100 rounded-lg shadow border border-gray-200 m-2 flex flex-col items-center justify-center';
                                errorDiv.innerHTML = `
                                  <svg class="w-12 h-12 text-gray-400 mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/>
                                  </svg>
                                  <p class="text-xs text-gray-500">Image ${idx + 1}</p>
                                  <a href="${url}" target="_blank" rel="noopener noreferrer" class="text-xs text-blue-500 mt-1 hover:underline">Open Link</a>
                                `;
                                parent.appendChild(errorDiv);
                              }
                            }}
                            crossOrigin="anonymous"
                          />
                        ) : isPDF ? (
                          // Show PDF icon with link
                          <div className="w-[150px] h-[150px] bg-gray-100 rounded-lg shadow border border-gray-200 m-2 flex flex-col items-center justify-center">
                            <svg className="w-12 h-12 text-red-500 mb-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                            </svg>
                            <p className="text-xs text-gray-600">PDF</p>
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 mt-1 hover:underline">View PDF</a>
                          </div>
                        ) : (
                          // Other file types
                          <div className="w-[150px] h-[150px] bg-gray-100 rounded-lg shadow border border-gray-200 m-2 flex flex-col items-center justify-center">
                            <svg className="w-12 h-12 text-gray-500 mb-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                            <p className="text-xs text-gray-600">File</p>
                            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 mt-1 hover:underline">View</a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {product.fields.bottleImage && (
                <div className="mb-4 relative">
                  {bottleImageLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
                    </div>
                  )}
                  <img
                    src={product.fields.bottleImage}
                    alt="Bottle"
                    style={{ width: '500px', height: 'auto' }}
                    onLoad={() => setBottleImageLoading(false)}
                    className={bottleImageLoading ? 'invisible' : 'visible'}
                  />
                </div>
              )}
              <div className="mb-4 grid text-sm grid-cols-3">
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Customer Name: </span>
                  <span className="text-lg font-bold text-gray-900">
                    {isVendor
                      ? `${(realTimeQuote.customerName || realTimeQuote.companyName)[0]}***${(realTimeQuote.customerName || realTimeQuote.companyName)[(realTimeQuote.customerName || realTimeQuote.companyName).length - 1]}`
                      : realTimeQuote.customerName || realTimeQuote.companyName || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Sales Rep Name: </span>
                  <span className="text-lg font-bold text-gray-900">{realTimeQuote.salesRepName}</span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Project Name: </span>
                  <span className="text-lg font-bold text-gray-900">
                    {isVendor
                      ? `${realTimeQuote.projectName[0]}***${
                          realTimeQuote.projectName[
                            realTimeQuote.projectName.length - 1
                          ]
                        }`
                      : realTimeQuote.projectName || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Project ID: </span>
                  <span className="text-lg font-bold text-gray-900">{realTimeQuote.projectId}</span>
                </div>
                {product.printingStyle === "Both" ? (
                    <div>
                      <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Printing Style: </span>
                      <span className="text-lg font-bold text-gray-900">CMYK & PMS Both</span>
                    </div>
                  ) : (
                    <div>
                      <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Printing Style: </span>
                      <span className="text-lg font-bold text-gray-900">{product.printingStyle}</span>
                    </div>
                  )}
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Production Type: </span>
                  <span className="text-lg font-bold text-gray-900">{product.productionType}</span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">SKU Count: </span>
                  <span className="text-lg font-bold text-gray-900">{product.SKU}</span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Quantities Per SKU: </span>
                  <span className="text-lg font-bold text-gray-900">{product.quantitiesCount}</span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Color Match: </span>
                  <span className="text-lg font-bold text-gray-900">{product.colorMatch}</span>
                </div>
                <div>
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Is this a Reprint?: </span>
                  <span className="text-lg font-bold text-gray-900">{product.isReprint}</span>
                </div>
                {realTimeQuote.accountStatus && (
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Account Status: </span>
                    <span className="text-lg font-bold text-gray-900">{realTimeQuote.accountStatus}</span>
                  </div>
                )}
                {product.isReprint === "Yes" && product.previousInvoiceNumber && (
                  <div>
                    <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Previous Invoice Number: </span>
                    <span className="text-lg font-bold text-gray-900">{product.previousInvoiceNumber}</span>
                  </div>
                )}
                {product.colorMatch === "Yes" && (
                  <>
                    {product.colorMatchOrderNumber && (
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Color Match Order Number: </span>
                        <span className="text-lg font-bold text-gray-900">{product.colorMatchOrderNumber}</span>
                      </div>
                    )}
                    {product.colorMatchDetails && (
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Color Match Details: </span>
                        <span className="text-lg font-bold text-gray-900">{product.colorMatchDetails}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {realTimeQuote.projectDescription && (
                <div className="mb-4">
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Quote Description: </span>
                  <span className="text-lg font-bold text-gray-900">{realTimeQuote.projectDescription}</span>
                </div>
              )}

              <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <span className="text-lg font-bold text-red-700 mb-2 block">Special Notes to Production: </span>
                <div className="text-base text-red-600 break-words whitespace-pre-wrap">
                  {product.specialNotesToProduction || "No special notes to production"}
                </div>
              </div>

              {/* Reference Image for Other product type */}
              {product.productType === 'Other' && product.fields.referenceImageUrl && (
                <div className="mb-4">
                  <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">Reference Image:</span>
                  <div className="mt-2">
                    <img
                      src={product.fields.referenceImageUrl}
                      alt="Reference"
                      className="max-w-full h-auto max-h-96 object-contain border border-gray-300 rounded-lg"
                    />
                    <div className="mt-2">
                      <a
                        href={product.fields.referenceImageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm underline"
                      >
                        Open Reference Image
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4 grid text-sm grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(product.fields)
                  .filter(([key, value]) => {
                    if (key === "bottleImage" || key === "artwork" || key === "referenceImageUrl") {
                      return false;
                    }
                    if (value === "" || value === null || value === undefined) {
                      return false;
                    }
                    if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) {
                      return false;
                    }
                    return true;
                  })
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-3">
                      <span className="block text-xs font-semibold text-gray-500 uppercase mb-1 tracking-wider">
                        {formatFieldName(key)}:{" "}
                      </span>
                      <span className="text-lg font-bold text-gray-900">
                        {Array.isArray(value) &&
                        (key === "bagSpecifications" ||
                          key === "specialFeatures" ||
                          key === "printingFeatures") ? (
                          <ul className="list-disc list-inside">
                            {value.map((item, index) => (
                              <li key={index}>{item || "N/A"}</li>
                            ))}
                          </ul>
                        ) : typeof value === "object" && value !== null ? (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm text-gray-500">Millimeters:</span>
                              <div className="space-y-1">
                                {value.widthMM && (
                                  <div>
                                    <span className="font-medium">Width:</span>
                                    <span className="ml-2">{value.widthMM}mm</span>
                                  </div>
                                )}
                                {value.heightMM && (
                                  <div>
                                    <span className="font-medium">Height:</span>
                                    <span className="ml-2">{value.heightMM}mm</span>
                                  </div>
                                )}
                                {value.lengthMM && (
                                  <div>
                                    <span className="font-medium">Length:</span>
                                    <span className="ml-2">{value.lengthMM}mm</span>
                                  </div>
                                )}
                                {value.gussetMM && (
                                  <div>
                                    <span className="font-medium">Gusset:</span>
                                    <span className="ml-2">{value.gussetMM}mm</span>
                                  </div>
                                )}
                                {value.depthMM && (
                                  <div>
                                    <span className="font-medium">Depth:</span>
                                    <span className="ml-2">{value.depthMM}mm</span>
                                  </div>
                                )}
                                {value.diameterMM && (
                                  <div>
                                    <span className="font-medium">Diameter:</span>
                                    <span className="ml-2">{value.diameterMM}mm</span>
                                  </div>
                                )}
                                {value.openingMM && (
                                  <div>
                                    <span className="font-medium">Opening:</span>
                                    <span className="ml-2">{value.openingMM}mm</span>
                                  </div>
                                )}
                                {value.thicknessMM && (
                                  <div>
                                    <span className="font-medium">Thickness:</span>
                                    <span className="ml-2">{value.thicknessMM}mm</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">Inches:</span>
                              <div className="space-y-1">
                                {value.width && (
                                  <div>
                                    <span className="font-medium">Width:</span>
                                    <span className="ml-2">{value.width}in</span>
                                  </div>
                                )}
                                {value.height && (
                                  <div>
                                    <span className="font-medium">Height:</span>
                                    <span className="ml-2">{value.height}in</span>
                                  </div>
                                )}
                                {value.length && (
                                  <div>
                                    <span className="font-medium">Length:</span>
                                    <span className="ml-2">{value.length}in</span>
                                  </div>
                                )}
                                {value.gusset && (
                                  <div>
                                    <span className="font-medium">Gusset:</span>
                                    <span className="ml-2">{value.gusset}in</span>
                                  </div>
                                )}
                                {value.depth && (
                                  <div>
                                    <span className="font-medium">Depth:</span>
                                    <span className="ml-2">{value.depth}in</span>
                                  </div>
                                )}
                                {value.diameter && (
                                  <div>
                                    <span className="font-medium">Diameter:</span>
                                    <span className="ml-2">{value.diameter}in</span>
                                  </div>
                                )}
                                {value.opening && (
                                  <div>
                                    <span className="font-medium">Opening:</span>
                                    <span className="ml-2">{value.opening}in</span>
                                  </div>
                                )}
                                {value.thickness && (
                                  <div>
                                    <span className="font-medium">Thickness:</span>
                                    <span className="ml-2">{value.thickness}in</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          formatFieldDisplayValue(key, value) || value || "N/A"
                        )}
                      </span>
                    </div>
                  ))}
              </div>

              <div className="mb-4">
                <h3 className="text-md font-semibold mb-2">Quantities</h3>
                <div className="overflow-x-auto rounded-xl shadow border border-gray-200">
                  <table className="min-w-full bg-white">
                    <thead className="sticky top-0 z-10">
                      <tr>
                        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-bold text-gray-700 uppercase tracking-wider rounded-tl-xl">SKU</th>
                        {(product.skuDetails?.[0]?.quantities || []).map((_, qtyIndex) => (
                          <th
                            key={qtyIndex}
                            className={`px-6 py-3 bg-gray-50 text-right text-xs font-bold text-gray-700 uppercase tracking-wider ${qtyIndex === (product.skuDetails?.[0]?.quantities?.length || 0) - 1 ? 'rounded-tr-xl' : ''}`}
                          >
                            Quantity {qtyIndex + 1}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(product.skuDetails || []).map((skuDetail, skuIndex) => (
                        <tr key={skuIndex} className={skuIndex % 2 === 0 ? 'bg-white hover:bg-blue-50 transition' : 'bg-gray-50 hover:bg-blue-50 transition'}>
                          <td className="px-6 py-4 text-gray-900 font-medium whitespace-nowrap">{skuDetail.sku}</td>
                          {(skuDetail.quantities || []).map((quantity, quantityIndex) => (
                            <td key={quantityIndex} className="px-6 py-4 text-right">
                              {isEditing ? (
                                <input
                                  type="number"
                                  value={quantity.value}
                                  onChange={(e) => handleQuantityChange(e, productIndex, skuIndex, quantityIndex)}
                                  className="w-20 text-right border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                                />
                              ) : (
                                quantity.value
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                      <tr className="bg-blue-50 font-bold">
                        <td className="px-6 py-4 text-gray-900">Total</td>
                        {(product.skuDetails?.[0]?.quantities || []).map((_, qtyIndex) => (
                          <td key={qtyIndex} className="px-6 py-4 text-right">
                            {(product.skuDetails || []).reduce((acc, skuDetail) => acc + parseInt(skuDetail.quantities?.[qtyIndex]?.value || 0, 10), 0)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                {realTimeQuote.createdBy === currentUserUid && (
                  <div className="mt-2">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          onClick={handleSaveQuantities}
                          className="mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-green-500 text-white hover:bg-green-700"
                          disabled={isSaving}
                        >
                          {isSaving ? 'Saving...' : 'Save Quantities'}
                        </button>
                        {isSaved && <p className="text-green-500 mt-2">Quantities saved successfully!</p>}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-blue-500 text-white hover:bg-blue-700"
                      >
                        Edit Quantities
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {userRole === 'PackagingAdmin' && (
          <div className="mb-4 mt-20">
            <h3 className="text-lg font-semibold mb-2">Upload Quote (PDF only)</h3>
            <div className="form-group">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Vendor Name:</label>
              <input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                type="text"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                placeholder="Vendor Name"
              />
            </div>
            <div className="form-group">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Product Type:</label>
              <select
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                required
              >
                <option value="">Select Product Type</option>
                <option value="Boxes">Boxes</option>
                <option value="Bottles">Bottles</option>
                <option value="Caps">Caps</option>
                <option value="Blisters">Blisters</option>
                <option value="ShrinkSleeves">Shrink Sleeves</option>
                <option value="Labels">Labels</option>
                <option value="Bags">Bags</option>
                <option value="Sachets">Sachets</option>
              </select>
            </div>
            <div className="form-group">
              <label className="block tracking-wide text-sm font-bold leading-6 text-gray-900">Upload PDF:</label>
              <input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                type="file"
                accept="application/pdf"
                onChange={(e) => setVendorPdf(e.target.files[0])}
              />
            </div>
            <button
              type="button"
              onClick={handleAddVendorDetails}
              className="mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-black text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-light"
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Add Vendor Details"}
            </button>
          </div>
        )}
        {realTimeQuote.vendorDetails && realTimeQuote.vendorDetails.length > 0 && (
          <div className="mt-4">
            <h3 className="text-lg font-semibold mb-2">Vendor Details</h3>
            {realTimeQuote.vendorDetails.map((vendor, index) => (
              <div key={index} className="mb-2">
                <span className='tracking-wide font-bold leading-6 text-gray-900'>Vendor Name: </span>
                <p>{vendor.vendorName}</p>
                <span className='tracking-wide font-bold leading-6 text-gray-900'>Product Type: </span>
                <p>{vendor.productType}</p>
                {(userRole === 'PackagingAdmin' || userRole === 'ShippingAdmin') && (
                  <>
                <span className='tracking-wide font-bold leading-6 text-gray-900'>PDF: </span>
                <a href={vendor.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">View PDF</a>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() => navigate('/packaging-details')}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-black text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-light"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={handleDownloadPDF}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md bg-black text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-light"
          >
            Download Requirement
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetails;
